import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import Papa from 'papaparse';
import { usePlatform } from '../context/PlatformContext';
import { parseExcelFile } from '../utils/excelParser';
import { generateCredentialId, generateVerificationId, extractInternId, ID_KEYS } from '../utils/idGenerator';
import { downloadCertificateWithTemplate } from '../utils/pdfGenerator';
import {
  sendSingleCandidateEmail,
  sendBulkCandidateEmails,
  validateCandidateForEmail,
  getBrevoStatus
} from '../utils/emailService';
import {
  buildEmailData, resolveTemplate,
  DEFAULT_INTERNSHIP_SUBJECT, DEFAULT_INTERNSHIP_BODY,
  DEFAULT_CERTIFICATE_SUBJECT, DEFAULT_CERTIFICATE_BODY
} from '../utils/emailTemplates';
import StatusBadge from '../components/ui/StatusBadge';
import {
  FiUploadCloud, FiMail, FiTrash2, FiDownload, FiCheck, FiAlertTriangle,
  FiAlertCircle, FiFile, FiPackage, FiEye, FiX, FiRefreshCw,
  FiChevronDown, FiChevronUp, FiExternalLink, FiClock, FiCheckCircle,
  FiSend, FiPlay, FiStopCircle, FiShield, FiLayout
} from 'react-icons/fi';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_KEYS = ['name', 'full name', 'fullname', 'student name', 'candidate name', 'intern name'];
const EMAIL_KEYS = ['email', 'email address', 'e-mail', 'mail', 'emailid', 'email id'];

function findCol(headers, options) {
  return headers.find(h => options.includes(h.toLowerCase().trim())) || null;
}

// ── Step indicator ────────────────────────────────────────────────
function Step({ n, label, done, active }) {
  return (
    <div className={`flex items-center gap-3 ${active ? 'opacity-100' : done ? 'opacity-80' : 'opacity-30'}`}>
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm font-semibold flex-shrink-0 transition-all
        ${done ? 'bg-emerald-500 text-white shadow-sm' : active ? 'bg-brand-600 text-white ring-4 ring-brand-500/15' : 'bg-surface-200 text-slate-400'}`}>
        {done ? <FiCheck className="w-4 h-4" /> : n}
      </div>
      <span className={`text-sm font-medium ${active ? 'text-navy-900' : done ? 'text-emerald-600' : 'text-slate-400'}`}>{label}</span>
    </div>
  );
}

// ── Individual Brevo Email Compose Modal ──────────────────────────
function BrevoEmailModal({ candidate, settings, mode, templateUrl, onClose, onSent }) {
  const [sending, setSending] = useState(false);
  const [attachPDF, setAttachPDF] = useState(true);

  const emailData = buildEmailData(candidate, settings, mode);
  const defaultSubject = settings?.emailSubject || (mode === 'internship' ? DEFAULT_INTERNSHIP_SUBJECT : DEFAULT_CERTIFICATE_SUBJECT);
  const defaultBody = settings?.emailBody || (mode === 'internship' ? DEFAULT_INTERNSHIP_BODY : DEFAULT_CERTIFICATE_BODY);

  const [subject, setSubject] = useState(resolveTemplate(defaultSubject, emailData));
  const [body, setBody] = useState(resolveTemplate(defaultBody, emailData));

  const credId = candidate.generatedId || candidate.verificationId || 'IH-FS-2026-0001';
  const cleanName = (candidate.name || 'Candidate').replace(/\s+/g, '_');
  const pdfFilename = `${cleanName}_${credId}_${mode === 'internship' ? 'Offer_Letter' : 'Certificate'}.pdf`;

  const handleSend = async () => {
    setSending(true);
    const toastId = toast.loading(`Sending email to ${candidate.name} via Brevo API...`);
    try {
      const result = await sendSingleCandidateEmail({
        candidate,
        settings,
        mode,
        templateUrl,
        customSubject: subject,
        customBody: body,
        attachPDF,
      });

      toast.success(`✅ Email sent to ${candidate.name} via Brevo!`, { id: toastId });
      onSent(candidate.id, {
        emailStatus: 'sent',
        brevoMessageId: result.messageId,
        lastEmailSentAt: result.sentAt,
        lastError: null,
      }, {
        id: result.messageId || crypto.randomUUID(),
        candidateId: candidate.id,
        candidateName: candidate.name,
        to: candidate.email,
        subject,
        body,
        attachPDF,
        status: 'sent',
        sentAt: result.sentAt,
        mode,
      });
      onClose();
    } catch (error) {
      console.error(error);
      const errMsg = error.message || 'Failed to send email';
      toast.error(`❌ Send failed: ${errMsg}`, { id: toastId });
      onSent(candidate.id, {
        emailStatus: 'failed',
        lastError: errMsg,
      }, {
        id: crypto.randomUUID(),
        candidateId: candidate.id,
        candidateName: candidate.name,
        to: candidate.email,
        subject,
        body,
        attachPDF,
        status: 'failed',
        errorMessage: errMsg,
        sentAt: new Date().toISOString(),
        mode,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-900/30 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="relative bg-white rounded-2xl w-full max-w-xl shadow-elevated border border-surface-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-5 border-b border-surface-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-sky-50 flex items-center justify-center">
              <FiMail className="text-sky-600 w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-navy-900">Send Personalized Email</h2>
                <span className="px-2 py-0.5 bg-sky-50 text-sky-700 border border-sky-100 rounded-lg text-[9px] font-black tracking-widest uppercase">
                  Brevo REST API
                </span>
              </div>
              <p className="text-xs text-slate-900 font-bold">{candidate.name} · {candidate.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700">
            <FiX className="w-4 h-4" />
          </button>
        </div>

        <div className="p-7 space-y-4 max-h-[80vh] overflow-y-auto">
          {/* To field */}
          <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
            <div className="flex items-center gap-3">
              <span className="section-title w-6">To</span>
              <span className="text-sm font-bold text-slate-800">{candidate.email}</span>
            </div>
            <code className="text-xs font-semibold text-brand-600 bg-sky-50 px-2.5 py-1 rounded-lg border border-sky-100">
              {credId}
            </code>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)}
              className="input-field" />
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Message Body (Personalized)</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={7}
              className="input-field resize-none font-mono leading-relaxed" />
          </div>

          {/* Attachment Info */}
          <div className="flex items-center justify-between p-3.5 bg-sky-50/50 border border-sky-100 rounded-2xl">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="modal-pdf-attach"
                checked={attachPDF}
                onChange={e => setAttachPDF(e.target.checked)}
                className="w-4 h-4 accent-sky-600 rounded cursor-pointer"
              />
              <label htmlFor="modal-pdf-attach" className="text-xs font-bold text-slate-700 cursor-pointer">
                Attach Personalized PDF: <span className="font-mono text-sky-600">{pdfFilename}</span>
              </label>
            </div>
            <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-100">
              Verified Match
            </span>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={sending}
              className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending}
              className="btn-primary flex-1 py-3 disabled:opacity-50">
              {sending ? (
                <>
                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                  Sending via Brevo...
                </>
              ) : (
                <>
                  <FiSend className="w-4 h-4" />
                  Send Email via Brevo API
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ── Send All Modal (Pre-validation, Live Queue, Completion) ────────
function SendAllModal({ candidates, settings, mode, templateUrl, onClose, onBatchFinished, onCandidateUpdated }) {
  const [phase, setPhase] = useState('confirm'); // 'confirm' | 'sending' | 'finished'
  const [progress, setProgress] = useState({
    total: 0,
    sent: 0,
    failed: 0,
    sending: 0,
    pending: 0,
    currentCandidate: null,
    results: [],
  });
  const cancellationRef = useRef({ isCancelled: false });

  // Pre-send validation calculations
  const totalRecipients = candidates.length;
  const docsReady = candidates.filter(c => c.docStatus === 'generated');
  const missingDocs = candidates.filter(c => c.docStatus !== 'generated');
  const invalidEmails = candidates.filter(c => !c.email || !EMAIL_REGEX.test(c.email.trim()));
  const validCandidates = candidates.filter(c => c.docStatus === 'generated' && c.email && EMAIL_REGEX.test(c.email.trim()));

  const startSending = async () => {
    if (validCandidates.length === 0) {
      return toast.error('No ready candidates with generated documents to send.');
    }

    setPhase('sending');
    cancellationRef.current.isCancelled = false;

    // Process every result exactly once using an index pointer.
    // This prevents the off-by-one miss where the last candidate's result
    // is never delivered to onCandidateUpdated via the intermediate callbacks.
    let lastProcessedIdx = 0;

    const applyNewResults = (results) => {
      for (let i = lastProcessedIdx; i < results.length; i++) {
        const latest = results[i];
        onCandidateUpdated(latest.candidateId, {
          emailStatus: latest.status,
          brevoMessageId: latest.messageId || null,
          lastEmailSentAt: latest.sentAt,
          lastError: latest.errorMessage || null,
        }, {
          id: latest.messageId || crypto.randomUUID(),
          candidateId: latest.candidateId,
          candidateName: latest.candidateName,
          to: latest.recipient,
          subject: latest.subject,
          body: latest.body,
          attachPDF: true,
          status: latest.status,
          errorMessage: latest.errorMessage,
          sentAt: latest.sentAt,
          mode,
        });
      }
      lastProcessedIdx = results.length;
    };

    const res = await sendBulkCandidateEmails({
      candidates: validCandidates,
      settings,
      mode,
      templateUrl,
      cancellationToken: cancellationRef.current,
      onProgress: (prog) => {
        setProgress(prog);
        // Apply any results that arrived since the last callback, including
        // the definitive final pass (prog.isFinal === true).
        applyNewResults(prog.results);
      },
    });

    setPhase('finished');
    onBatchFinished(res);
  };


  const handleRetryFailed = async () => {
    const failedIds = progress.results.filter(r => r.status === 'failed').map(r => r.candidateId);
    const toRetry = candidates.filter(c => failedIds.includes(c.id));
    if (toRetry.length === 0) return toast.success('No failed emails to retry!');

    setPhase('sending');
    cancellationRef.current.isCancelled = false;

    await sendBulkCandidateEmails({
      candidates: toRetry,
      settings,
      mode,
      templateUrl,
      cancellationToken: cancellationRef.current,
      onProgress: (prog) => {
        setProgress(prev => ({
          ...prev,
          sent: prev.sent + prog.sent,
          failed: prog.failed,
          currentCandidate: prog.currentCandidate,
          results: [...prev.results.filter(r => !failedIds.includes(r.candidateId)), ...prog.results],
        }));
      },
    });

    setPhase('finished');
  };

  const stopSending = () => {
    cancellationRef.current.isCancelled = true;
    toast('Stopping queue after current email finishes...', { icon: '⏸️' });
  };

  const pct = progress.total > 0 ? Math.round(((progress.sent + progress.failed) / progress.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={phase === 'sending' ? undefined : onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="relative bg-white rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden">

        {/* Modal Header */}
        <div className="flex items-center justify-between px-7 py-5 border-b border-surface-100 bg-slate-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white shadow-md shadow-sky-500/20">
              <FiSend className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-semibold text-navy-900 text-base">
                {phase === 'confirm' && '🚀 Send All Personalized Emails'}
                {phase === 'sending' && '⚡ Sending Emails via Brevo Queue'}
                {phase === 'finished' && '🎉 Email Dispatch Completed'}
              </h2>
              <p className="text-xs text-slate-900 font-bold">
                {mode === 'internship' ? 'Internship Offer Letters' : 'Certificates'} · Brevo Transactional REST API
              </p>
            </div>
          </div>
          {phase !== 'sending' && (
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700">
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Modal Content based on phase */}
        <div className="p-7 space-y-5">
          {/* ── PHASE 1: CONFIRMATION & PRE-VALIDATION ── */}
          {phase === 'confirm' && (
            <>
              <div className="text-sm text-slate-600 font-medium">
                Send personalized emails with individual PDF attachments to <span className="font-semibold text-navy-900">{validCandidates.length}</span> candidates via Brevo?
              </div>

              {/* Validation Summary Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="section-title">Total Candidates</div>
                  <div className="text-2xl font-semibold text-navy-900 mt-1">{totalRecipients}</div>
                </div>
                <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-100">
                  <div className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Ready to Send</div>
                  <div className="text-2xl font-black text-emerald-700 mt-1">{validCandidates.length}</div>
                </div>
                <div className={`p-4 rounded-2xl border ${missingDocs.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100'}`}>
                  <div className="section-title">Missing Documents</div>
                  <div className={`text-xl font-black mt-1 ${missingDocs.length > 0 ? 'text-amber-700' : 'text-slate-700'}`}>
                    {missingDocs.length}
                  </div>
                </div>
                <div className={`p-4 rounded-2xl border ${invalidEmails.length > 0 ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-100'}`}>
                  <div className="section-title">Missing / Invalid Email</div>
                  <div className={`text-xl font-black mt-1 ${invalidEmails.length > 0 ? 'text-rose-700' : 'text-slate-700'}`}>
                    {invalidEmails.length}
                  </div>
                </div>
              </div>

              {/* Warnings if any */}
              {(missingDocs.length > 0 || invalidEmails.length > 0) && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3 text-xs text-amber-800">
                  <FiAlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">Notice: </span>
                    Candidates without generated documents or with invalid email addresses will be automatically skipped to prevent failed sends.
                  </div>
                </div>
              )}

              {/* Sender info */}
              <div className="p-3.5 bg-sky-50/60 border border-sky-100 rounded-2xl text-xs text-sky-800 flex items-center gap-2.5">
                <FiShield className="w-4 h-4 text-sky-600 flex-shrink-0" />
                <span>
                  Emails will be dispatched securely from <strong className="font-bold">Innovation Hacks</strong> &lt;innovationhacksofficial@gmail.com&gt; via Brevo REST API.
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={onClose} className="px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold text-sm hover:bg-slate-200">
                  Cancel
                </button>
                <button
                  onClick={startSending}
                  disabled={validCandidates.length === 0}
                  className="flex items-center gap-2 px-8 py-3 bg-sky-600 text-white rounded-xl font-black text-sm hover:bg-sky-500 transition-all shadow-lg shadow-sky-500/20 disabled:opacity-50">
                  <FiPlay className="w-4 h-4" /> Confirm & Send ({validCandidates.length})
                </button>
              </div>
            </>
          )}

          {/* ── PHASE 2: REAL-TIME QUEUE & PROGRESS ── */}
          {phase === 'sending' && (
            <div className="space-y-5">
              {/* Counter pills */}
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="p-2.5 bg-slate-100 rounded-xl">
                  <div className="text-[9px] font-black text-slate-400 uppercase">Total</div>
                  <div className="text-lg font-semibold text-navy-900">{progress.total}</div>
                </div>
                <div className="p-2.5 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div className="text-[9px] font-black text-emerald-600 uppercase">Sent</div>
                  <div className="text-lg font-black text-emerald-700">{progress.sent}</div>
                </div>
                <div className="p-2.5 bg-sky-50 rounded-xl border border-sky-100">
                  <div className="text-[9px] font-semibold text-brand-600 uppercase">Sending</div>
                  <div className="text-lg font-black text-sky-700 animate-pulse">{progress.sending}</div>
                </div>
                <div className="p-2.5 bg-rose-50 rounded-xl border border-rose-100">
                  <div className="text-[9px] font-black text-rose-600 uppercase">Failed</div>
                  <div className="text-lg font-black text-rose-700">{progress.failed}</div>
                </div>
              </div>

              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-xs font-bold text-slate-600 mb-2">
                  <span>Progress</span>
                  <span>{progress.sent + progress.failed} / {progress.total} ({pct}%)</span>
                </div>
                <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.2 }}
                  />
                </div>
              </div>

              {/* Current candidate info */}
              {progress.currentCandidate && (
                <div className="p-4 bg-sky-50 border border-sky-100 rounded-2xl flex items-center gap-3">
                  <FiRefreshCw className="w-5 h-5 text-sky-600 animate-spin flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-brand-900 truncate">
                      Generating PDF & sending to: {progress.currentCandidate.name}
                    </div>
                    <div className="text-[10px] text-sky-600 font-mono truncate">
                      {progress.currentCandidate.email} · {progress.currentCandidate.generatedId || progress.currentCandidate.verificationId}
                    </div>
                  </div>
                </div>
              )}

              {/* Stop button */}
              <div className="flex justify-end pt-2">
                <button
                  onClick={stopSending}
                  className="flex items-center gap-2 px-6 py-2.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl font-bold text-xs hover:bg-rose-100 transition-all">
                  <FiStopCircle className="w-4 h-4" /> Stop Queue
                </button>
              </div>
            </div>
          )}

          {/* ── PHASE 3: SUMMARY & RETRY ── */}
          {phase === 'finished' && (
            <div className="space-y-5">
              <div className="text-center py-4">
                <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-inner">
                  <FiCheck className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-semibold text-navy-900">Queue Processing Finished</h3>
                <p className="text-xs text-slate-500 mt-1">All batches processed through Brevo REST API</p>
              </div>

              <div className="flex gap-3 justify-center">
                <div className="flex-1 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl text-center">
                  <div className="text-xs font-bold text-emerald-600">✓ Successfully Sent</div>
                  <div className="text-2xl font-black text-emerald-700 mt-1">{progress.sent}</div>
                </div>
                {progress.failed > 0 && (
                  <div className="flex-1 p-4 bg-rose-50 border border-rose-100 rounded-2xl text-center">
                    <div className="text-xs font-bold text-rose-600">✕ Failed</div>
                    <div className="text-2xl font-black text-rose-700 mt-1">{progress.failed}</div>
                  </div>
                )}
              </div>

              {/* Failed candidates list if any */}
              {progress.failed > 0 && (
                <div className="max-h-36 overflow-y-auto rounded-xl border border-rose-100 bg-rose-50/50 p-3 text-xs space-y-1">
                  <div className="font-bold text-rose-800 text-[10px] uppercase tracking-wider mb-1">Failed Recipients:</div>
                  {progress.results.filter(r => r.status === 'failed').map((r, i) => (
                    <div key={i} className="flex justify-between items-center text-rose-700">
                      <span>{r.candidateName} ({r.recipient})</span>
                      <span className="text-[10px] font-mono text-rose-500 truncate max-w-[160px]">{r.errorMessage}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex justify-end gap-3 pt-2">
                {progress.failed > 0 && (
                  <button
                    onClick={handleRetryFailed}
                    className="flex items-center gap-2 px-6 py-3 bg-rose-600 text-white rounded-xl font-bold text-sm hover:bg-rose-500 shadow-md shadow-rose-500/20">
                    <FiRefreshCw className="w-4 h-4" /> Retry Failed ({progress.failed})
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="px-8 py-3 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-all">
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ── Candidate Row Component ───────────────────────────────────────
function CandidateRow({
  candidate,
  index,
  settings,
  mode,
  templateUrl,
  onDelete,
  onDownload,
  onOpenEmailModal,
  onDirectSend,
  onRegenerate,
  sendingId,
  isSelected,
  onToggleSelect
}) {
  const [expanded, setExpanded] = useState(false);
  const isGenerated = candidate.docStatus === 'generated';
  const isSending = sendingId === candidate.id;
  const emailStatus = candidate.emailStatus || 'pending';

  return (
    <>
      <motion.tr initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.03 }}
        className={`group transition-all duration-200 ${
          isSelected
            ? 'bg-brand-50/60 shadow-sm ring-1 ring-brand-300'
            : 'bg-white hover:bg-surface-50 hover:shadow-card'
        }`}>
        {/* Row Checkbox */}
        <td className="px-5 py-5 w-12 text-center rounded-l-2xl border-y border-l border-surface-200/90 bg-inherit">
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={() => onToggleSelect && onToggleSelect(candidate.id)}
            className="w-4.5 h-4.5 rounded text-brand-600 focus:ring-brand-500 border-surface-300 cursor-pointer accent-brand-600"
          />
        </td>

        {/* Index */}
        <td className="px-3 py-5 w-10 text-center border-y border-surface-200/90 bg-inherit">
          <span className="text-xs font-bold text-slate-400">
            {index + 1}
          </span>
        </td>

        {/* Candidate info with CIRCULAR AVATAR */}
        <td className="px-5 py-5 border-y border-surface-200/90 bg-inherit">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-600 via-brand-700 to-navy-900 flex items-center justify-center text-white text-xs font-black flex-shrink-0 shadow-sm ring-2 ring-brand-100">
              {candidate.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-bold text-slate-950 text-sm tracking-tight leading-snug">{candidate.name}</div>
              <div className="text-xs text-slate-700 font-semibold mt-0.5 font-mono">{candidate.email}</div>
              {(candidate.college || candidate.role || candidate.department) && (
                <div className="text-[11px] font-semibold text-slate-500 mt-1 truncate max-w-[220px]">
                  {[candidate.college, candidate.department, candidate.role].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Credential ID */}
        <td className="px-5 py-5 border-y border-surface-200/90 bg-inherit">
          {isGenerated ? (
            <div className="flex items-center gap-2">
              <code className="text-xs font-black text-brand-700 bg-brand-50 border border-brand-200 px-3 py-1.5 rounded-xl font-mono tracking-tight shadow-2xs">
                {candidate.generatedId}
              </code>
              <button onClick={() => { navigator.clipboard.writeText(candidate.generatedId); toast.success('ID copied!'); }}
                className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-brand-600 transition-all text-[11px] font-bold">
                Copy
              </button>
            </div>
          ) : (
            <span className="text-xs text-slate-400 font-bold italic">Not generated</span>
          )}
        </td>

        {/* Document Status */}
        <td className="px-5 py-5 border-y border-surface-200/90 bg-inherit">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border
            ${isGenerated
              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
              : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
            {isGenerated ? <><FiCheck className="w-3 h-3" /> Ready</> : <><FiClock className="w-3 h-3" /> Pending</>}
          </span>
        </td>

        {/* Actions */}
        <td className="px-6 py-5 rounded-r-2xl border-y border-r border-surface-200/90 bg-inherit text-right">
          <div className="flex items-center gap-2.5 justify-end">
            {isGenerated ? (
              <>
                {/* Download PDF */}
                <button onClick={() => onDownload(candidate)}
                  title="Download PDF"
                  className="h-10 px-4 bg-navy-900 hover:bg-navy-800 text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-1.5">
                  <FiDownload className="w-3.5 h-3.5" /> PDF
                </button>

                {/* Regenerate */}
                <button onClick={() => onRegenerate(candidate)}
                  title="Regenerate ID"
                  className="h-10 w-10 text-slate-500 hover:text-violet-700 hover:bg-violet-50 rounded-xl border border-surface-200 transition-all flex items-center justify-center">
                  <FiRefreshCw className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <span className="text-xs text-slate-800 font-bold italic px-2">Generate doc first</span>
            )}

            {/* Prominent Red Delete Button */}
            <button onClick={() => onDelete(candidate.id)}
              title="Delete candidate"
              className="h-10 w-10 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white border border-red-200 rounded-xl transition-all shadow-2xs flex items-center justify-center flex-shrink-0">
              <FiTrash2 className="w-4 h-4" />
            </button>

            {/* Expand */}
            <button onClick={() => setExpanded(!expanded)}
              className="h-10 w-10 text-slate-600 hover:text-slate-950 hover:bg-surface-100 rounded-xl transition-all flex items-center justify-center">
              {expanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </td>
      </motion.tr>

      {/* Expanded details */}
      <AnimatePresence>
        {expanded && (
          <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <td colSpan={6} className="bg-slate-50/80 border-b border-surface-100">
              <div className="px-8 py-4 grid grid-cols-3 gap-6 text-xs">
                {[
                  { label: 'Email', val: candidate.email },
                  { label: 'College', val: candidate.college || '—' },
                  { label: 'Role', val: candidate.role || '—' },
                  { label: 'Department', val: candidate.department || '—' },
                  { label: 'Verify ID', val: candidate.verificationId || '—' },
                  { label: 'Created', val: new Date(candidate.createdAt).toLocaleDateString() },
                  { label: 'Brevo Msg ID', val: candidate.brevoMessageId || '—' },
                  { label: 'Last Error', val: candidate.lastError || 'None' },
                ].map(item => (
                  <div key={item.label}>
                    <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{item.label}</div>
                    <div className="font-bold text-slate-700 truncate font-mono">{item.val}</div>
                  </div>
                ))}
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
}

// ── Main Candidates Page ──────────────────────────────────────────
export default function Candidates() {
  const {
    mode,
    candidates,
    addCandidates,
    updateCandidate,
    deleteCandidate,
    deleteAllCandidates,
    settings,
    uploadCustomTemplate,
    addEmailToHistory,
    customTemplatesMap
  } = usePlatform();

  const activeCustomTemplate = customTemplatesMap?.[settings?.template]?.url;
  const activeCustomName = customTemplatesMap?.[settings?.template]?.name;

  // Template state — preloaded with active template from studio or Innovation Hacks template
  const [templateFile, setTemplateFile] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [templateType, setTemplateType] = useState('image');
  const templateInputRef = useRef(null);

  const effectiveTemplateUrl = templatePreview || activeCustomTemplate || (mode === 'certificate' ? '/Certificate.jpeg' : '/template.png');
  const effectiveTemplateName = templateFile?.name || activeCustomName || (settings?.template === 'completion_v1' ? 'Completion_Certificate.png' : settings?.template === 'excellence_v1' ? 'Excellence_Award.png' : 'Innovation_Hacks_Offer_Letter.png');

  // Dataset state
  const [previewRows, setPreviewRows] = useState(null);
  const datasetInputRef = useRef(null);

  // Email modals & queue state
  const [emailModalCandidate, setEmailModalCandidate] = useState(null);
  const [showSendAllModal, setShowSendAllModal] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [sendingCandidateId, setSendingCandidateId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Checkbox helpers
  const toggleSelectAll = () => {
    if (selectedIds.size === candidates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(candidates.map(c => c.id)));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (!selectedIds.size) return;
    if (confirm(`Delete ${selectedIds.size} selected candidate(s)?`)) {
      selectedIds.forEach(id => deleteCandidate(id));
      setSelectedIds(new Set());
      toast.success('Selected candidates deleted');
    }
  };

  // Template drag-over
  const [tplDrag, setTplDrag] = useState(false);
  const [dsDrag, setDsDrag] = useState(false);

  // ── Steps ─────────────────────────────────────────────────────
  const hasTemplate = true; // Always active from Templates studio!
  const hasDataset = candidates.length > 0;
  const allGenerated = hasDataset && candidates.every(c => c.docStatus === 'generated');
  const pendingGenCount = candidates.filter(c => c.docStatus === 'pending').length;
  const readyToSendCount = candidates.filter(c => c.docStatus === 'generated').length;

  // ── Template Upload ───────────────────────────────────────────
  const handleTemplateFile = (file) => {
    if (!file) return;
    const isImg = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    if (!isImg && !isPdf) return toast.error('Please upload a PDF or image (PNG, JPG) file.');
    setTemplateFile(file);
    setTemplateType(isPdf ? 'pdf' : 'image');
    const url = URL.createObjectURL(file);
    setTemplatePreview(url);
    uploadCustomTemplate(file);
    toast.success(`Template "${file.name}" loaded!`);
  };

  // ── Dataset Upload ─────────────────────────────────────────────
  const processRows = (rawRows) => {
    const headers = Object.keys(rawRows[0] || {});
    const nameKey = findCol(headers, NAME_KEYS);
    const emailKey = findCol(headers, EMAIL_KEYS);
    const idKey = findCol(headers, ID_KEYS);
    if (!nameKey || !emailKey) {
      toast.error(`Could not find Name/Email columns. Found: ${headers.join(', ')}`);
      return null;
    }
    const existingEmails = new Set(candidates.map(c => c.email?.toLowerCase()));
    return rawRows
      .filter(r => r[nameKey]?.trim())
      .map(r => ({
        ...r,
        _name: r[nameKey]?.trim(),
        _email: r[emailKey]?.trim(),
        _internId: extractInternId(r, headers) || (idKey ? r[idKey]?.trim() : null),
        _emailValid: EMAIL_REGEX.test(r[emailKey]?.trim() || ''),
        _isDuplicate: existingEmails.has(r[emailKey]?.toLowerCase().trim()),
      }));
  };

  const handleDatasetFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'csv') {
      Papa.parse(file, {
        header: true, skipEmptyLines: true,
        complete: ({ data }) => {
          const rows = processRows(data);
          if (rows) setPreviewRows(rows);
        }
      });
    } else if (['xlsx', 'xls'].includes(ext)) {
      toast.loading('Parsing Excel...', { id: 'xl' });
      try {
        const data = await parseExcelFile(file);
        toast.dismiss('xl');
        const rows = processRows(data);
        if (rows) setPreviewRows(rows);
      } catch { toast.error('Excel parse failed', { id: 'xl' }); }
    } else {
      toast.error('Upload CSV or Excel file.');
    }
  };

  const confirmImport = () => {
    if (!previewRows) return;
    const valid = previewRows.filter(r => !r._isDuplicate && r._emailValid);
    const dupes = previewRows.filter(r => r._isDuplicate).length;
    const invalid = previewRows.filter(r => !r._emailValid).length;
    const newCandidates = valid.map(row => ({
      id: crypto.randomUUID(),
      name: row._name, email: row._email,
      college: row.College || row.college || row.Institution || '',
      role: row.Role || row.role || row.Position || '',
      department: row.Department || row.department || '',
      duration: row.Duration || row.duration || '1 Month',
      startDate: row.StartDate || row['Start Date'] || '2026-08-22',
      endDate: row.EndDate || row['End Date'] || '2026-09-22',
      docStatus: row._internId ? 'generated' : 'pending',
      emailStatus: 'pending',
      certStatus: row._internId ? 'active' : 'pending',
      generatedId: row._internId || null,
      verificationId: row._internId || generateVerificationId(mode === 'internship' ? 'INT' : 'CERT'),
      createdAt: new Date().toISOString(), mode,
      expiryDate: new Date(Date.now() + (settings?.expiryDays || 365) * 86400000).toISOString(),
      raw: row,
    }));
    addCandidates(newCandidates);
    const msgs = [`✅ ${newCandidates.length} imported`];
    if (dupes) msgs.push(`⚠️ ${dupes} duplicates skipped`);
    if (invalid) msgs.push(`❌ ${invalid} invalid emails skipped`);
    toast.success(msgs.join(' · '));
    setPreviewRows(null);
  };

  // ── Document Generation ─────────────────────────────────────────
  const generateSingle = async (candidate) => {
    const idx = candidates.findIndex(c => c.id === candidate.id);
    updateCandidate(candidate.id, { docStatus: 'generating' });
    await new Promise(r => setTimeout(r, 300));
    // CRITICAL: Preserve existing candidate.generatedId if already assigned (e.g. from upload)
    const genId = candidate.generatedId || generateCredentialId(settings, idx);
    updateCandidate(candidate.id, {
      docStatus: 'generated', generatedId: genId,
      certStatus: 'active', generatedAt: new Date().toISOString()
    });
    return genId;
  };

  const generateAll = async () => {
    const pending = candidates.filter(c => c.docStatus === 'pending' || c.docStatus === 'generating');
    if (!pending.length) return toast.error('All documents already generated!');
    toast.loading(`Generating ${pending.length} documents...`, { id: 'bulk-gen' });
    for (let i = 0; i < pending.length; i++) {
      await new Promise(r => setTimeout(r, 100));
      // CRITICAL: Preserve existing generatedId if already assigned
      const genId = pending[i].generatedId || generateCredentialId(settings, candidates.indexOf(candidates.find(c => c.id === pending[i].id)));
      updateCandidate(pending[i].id, {
        docStatus: 'generated', generatedId: genId,
        certStatus: 'active', generatedAt: new Date().toISOString()
      });
    }
    toast.success(`✅ ${pending.length} documents generated!`, { id: 'bulk-gen' });
  };

  const downloadPDF = async (candidate) => {
    const id = `pdf-${candidate.id}`;
    toast.loading('Creating PDF...', { id });
    try {
      // Use effectiveTemplateUrl so the saved custom template is always used,
      // not just a local preview blob that disappears on page refresh.
      await downloadCertificateWithTemplate(candidate, effectiveTemplateUrl, settings, mode);
      toast.success('PDF downloaded!', { id });
    } catch (e) {
      console.error(e);
      toast.error('PDF failed.', { id });
    }
  };

  const downloadAll = async () => {
    const generated = candidates.filter(c => c.docStatus === 'generated');
    if (!generated.length) return toast.error('Generate documents first!');
    toast.loading(`Creating ZIP of ${generated.length} PDFs...`, { id: 'zip' });
    try {
      const { downloadBulkZIP } = await import('../utils/zipExporter');
      await downloadBulkZIP(generated, settings, mode, effectiveTemplateUrl);
      toast.success('ZIP downloaded!', { id: 'zip' });
    } catch (e) {
      console.error(e);
      toast.error('ZIP failed.', { id: 'zip' });
    }
  };

  // Direct single email send (without modal)
  const handleDirectSend = async (candidate) => {
    setSendingCandidateId(candidate.id);
    const toastId = toast.loading(`Sending email & PDF to ${candidate.name} via Brevo...`);
    try {
      const result = await sendSingleCandidateEmail({
        candidate,
        settings,
        mode,
        templateUrl: effectiveTemplateUrl,
        attachPDF: true,
      });

      toast.success(`✅ Email sent to ${candidate.name}!`, { id: toastId });
      updateCandidate(candidate.id, {
        emailStatus: 'sent',
        brevoMessageId: result.messageId,
        lastEmailSentAt: result.sentAt,
        lastError: null,
      });
      addEmailToHistory({
        id: result.messageId || crypto.randomUUID(),
        candidateId: candidate.id,
        candidateName: candidate.name,
        to: candidate.email,
        subject: result.subject,
        body: result.body,
        attachPDF: true,
        status: 'sent',
        sentAt: result.sentAt,
        mode,
      });
    } catch (error) {
      const errMsg = error.message || 'Failed to send email';
      toast.error(`❌ Failed: ${errMsg}`, { id: toastId });
      updateCandidate(candidate.id, {
        emailStatus: 'failed',
        lastError: errMsg,
      });
      addEmailToHistory({
        id: crypto.randomUUID(),
        candidateId: candidate.id,
        candidateName: candidate.name,
        to: candidate.email,
        subject: settings.emailSubject || 'Offer Letter',
        body: settings.emailBody || '',
        attachPDF: true,
        status: 'failed',
        errorMessage: errMsg,
        sentAt: new Date().toISOString(),
        mode,
      });
    } finally {
      setSendingCandidateId(null);
    }
  };

  const handleCandidateEmailUpdate = (candidateId, updates, historyRecord) => {
    updateCandidate(candidateId, updates);
    if (historyRecord) {
      addEmailToHistory(historyRecord);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="page-title capitalize">{mode} Registry</h1>
          <p className="page-subtitle">Upload dataset → Generate documents → Send via Brevo API</p>
        </div>
        {/* Steps indicator */}
        <div className="flex items-center gap-5">
          <Step n="1" label="Template Ready" done={true} active={false} />
          <div className="w-8 h-px bg-slate-200" />
          <Step n="2" label="Dataset" done={hasDataset} active={!hasDataset} />
          <div className="w-8 h-px bg-slate-200" />
          <Step n="3" label="Generate" done={allGenerated} active={hasDataset && !allGenerated} />
        </div>
      </div>

      {/* ── Step 1: Active Template (Synced from Templates Studio) ── */}
      <div className="glass-card rounded-2xl overflow-hidden border border-emerald-200/80 bg-white shadow-2xs">
        <div className="p-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center font-black shadow-xs flex-shrink-0">
                <FiCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-bold text-slate-950 text-base">
                    Active Template: <span className="text-brand-700 font-black">{effectiveTemplateName}</span>
                  </h2>
                  <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md text-[10px] font-black uppercase">
                    Ready to Generate
                  </span>
                </div>
                <p className="text-slate-600 text-xs font-semibold mt-0.5">
                  Synchronized with the Templates Studio. Names, IDs, and cryptographic QR codes will be overlaid automatically.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <input type="file" accept="image/*,.pdf" className="hidden" ref={templateInputRef}
                onChange={e => handleTemplateFile(e.target.files[0])} />
              
              <a
                href="#/templates"
                className="h-10 px-4 bg-brand-50 hover:bg-brand-100 text-brand-700 border border-brand-200 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 shadow-2xs"
              >
                <FiLayout className="w-3.5 h-3.5" /> Manage in Templates Studio
              </a>

              <button
                type="button"
                onClick={() => templateInputRef.current?.click()}
                className="h-10 px-4 bg-surface-100 hover:bg-surface-200 text-slate-800 border border-surface-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
              >
                <FiRefreshCw className="w-3.5 h-3.5" /> Quick Override
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Step 2: Dataset Upload ─────────────────────────────── */}
      <div className={`glass-card rounded-2xl overflow-hidden transition-all ${hasDataset ? 'border border-emerald-100' : hasTemplate ? 'border-2 border-dashed border-sky-200' : 'border-2 border-dashed border-slate-150 opacity-60'}`}>
        <div className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black ${hasDataset ? 'bg-emerald-500 text-white' : hasTemplate ? 'bg-sky-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {hasDataset ? <FiCheck className="w-3.5 h-3.5" /> : '2'}
                </div>
                <h2 className="font-semibold text-navy-900">Upload Candidate Dataset</h2>
                {hasDataset && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-lg text-[9px] font-black uppercase">{candidates.length} Imported</span>}
              </div>
              <p className="text-slate-400 text-sm ml-8">CSV or Excel with Name + Email columns. Auto-mapped, duplicates detected.</p>
            </div>
            <div className="flex gap-2">
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" ref={datasetInputRef}
                onChange={e => handleDatasetFile(e.target.files[0])} />
              <button onClick={() => datasetInputRef.current?.click()} disabled={!hasTemplate}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-black transition-all
                  ${hasTemplate ? 'bg-sky-600 text-white hover:bg-sky-500 shadow-lg shadow-sky-500/20' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
                <FiUploadCloud className="w-4 h-4" /> {hasDataset ? 'Import More' : 'Upload Dataset'}
              </button>
            </div>
          </div>

          {/* Drop zone */}
          {!hasDataset && hasTemplate && (
            <div
              className={`mt-4 ml-8 border-2 border-dashed rounded-2xl p-8 flex flex-col items-center cursor-pointer transition-all
                ${dsDrag ? 'border-sky-400 bg-sky-50/50' : 'border-sky-200 hover:border-sky-300'}`}
              onDragOver={e => { e.preventDefault(); setDsDrag(true); }}
              onDragLeave={() => setDsDrag(false)}
              onDrop={e => { e.preventDefault(); setDsDrag(false); handleDatasetFile(e.dataTransfer.files[0]); }}
              onClick={() => datasetInputRef.current?.click()}
            >
              <FiUploadCloud className={`w-8 h-8 mb-2 ${dsDrag ? 'text-sky-400' : 'text-sky-300'}`} />
              <p className="text-sm text-sky-600 font-bold">Drag & drop CSV / Excel here</p>
              <p className="text-[10px] text-slate-300 font-bold uppercase tracking-widest mt-1">Must have Name & Email columns</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Step 3: Generate + Candidate Table + SEND ALL ───────── */}
      {hasDataset && (
        <div className="glass-card rounded-2xl overflow-hidden shadow-sm">
          {/* Table header with actions */}
          <div className="p-5 border-b border-surface-100 flex flex-wrap items-center justify-between gap-4 bg-slate-50/60">
            <div className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black ${allGenerated ? 'bg-emerald-500 text-white' : 'bg-sky-500 text-white'}`}>
                {allGenerated ? <FiCheck className="w-3.5 h-3.5" /> : '3'}
              </div>
              <div>
                <h2 className="font-semibold text-navy-900 text-sm">
                  {candidates.length} Candidate{candidates.length !== 1 ? 's' : ''}
                </h2>
                <p className="text-[10px] text-slate-700 font-bold font-bold">
                  {candidates.filter(c => c.docStatus === 'generated').length} generated · {pendingGenCount} pending generation
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              {/* 🚀 SEND ALL BUTTON */}
              <button
                onClick={() => setShowSendAllModal(true)}
                disabled={readyToSendCount === 0}
                className="btn-primary text-xs disabled:opacity-50">
                <FiSend className="w-4 h-4" /> 🚀 SEND ALL ({readyToSendCount})
              </button>

              {/* Generate All */}
              {pendingGenCount > 0 && (
                <button onClick={generateAll}
                  className="btn-primary text-xs">
                  <FiFile className="w-4 h-4" /> Generate All ({pendingGenCount})
                </button>
              )}

              {/* Bulk ZIP */}
              {candidates.some(c => c.docStatus === 'generated') && (
                <button onClick={downloadAll}
                  className="btn-primary text-xs bg-navy-900 hover:bg-navy-800">
                  <FiPackage className="w-4 h-4" /> Bulk ZIP
                </button>
              )}

              {/* Import More */}
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" ref={datasetInputRef}
                onChange={e => handleDatasetFile(e.target.files[0])} />
              <button onClick={() => datasetInputRef.current?.click()}
                className="btn-secondary text-xs">
                <FiUploadCloud className="w-3.5 h-3.5" /> Import More
              </button>

              {/* Delete All Candidates */}
              {candidates.length > 0 && (
                <button
                  onClick={() => setShowDeleteAllConfirm(true)}
                  className="btn-danger text-xs font-bold shadow-2xs flex items-center gap-1.5">
                  <FiTrash2 className="w-3.5 h-3.5" /> Delete All ({candidates.length})
                </button>
              )}
            </div>
          </div>

          {/* Floating Batch Action Bar */}
          {selectedIds.size > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-brand-50 border border-brand-200 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-4 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-xl bg-brand-600 text-white font-black text-xs flex items-center justify-center shadow-xs">
                  {selectedIds.size}
                </span>
                <div>
                  <div className="font-bold text-slate-950 text-sm">{selectedIds.size} Candidate(s) Selected</div>
                  <div className="text-xs text-slate-600 font-semibold">Perform batch operations on selected candidates</div>
                </div>
              </div>

              <div className="flex items-center gap-2.5 flex-wrap">
                <button
                  onClick={handleDeleteSelected}
                  className="btn-danger text-xs py-2 px-4 font-bold flex items-center gap-1.5 shadow-2xs"
                >
                  <FiTrash2 className="w-3.5 h-3.5" /> Delete Selected ({selectedIds.size})
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="btn-secondary text-xs py-2 px-4 font-bold"
                >
                  Deselect All
                </button>
              </div>
            </motion.div>
          )}

          {/* Table */}
          <div className="overflow-x-auto p-4 bg-surface-50/50">
            <table className="w-full text-left border-separate border-spacing-y-3.5">
              <thead>
                <tr>
                  {/* Select All Checkbox */}
                  <th className="px-5 py-3 w-14 text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.size > 0 && selectedIds.size === candidates.length}
                      ref={el => {
                        if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < candidates.length;
                      }}
                      onChange={toggleSelectAll}
                      className="w-4.5 h-4.5 rounded text-brand-600 focus:ring-brand-500 border-surface-300 cursor-pointer accent-brand-600"
                      title="Select / Deselect All"
                    />
                  </th>
                  <th className="px-3 py-3 section-title w-10 text-center">#</th>
                  <th className="px-5 py-3 section-title">Candidate</th>
                  <th className="px-5 py-3 section-title">Credential ID</th>
                  <th className="px-5 py-3 section-title">Doc Status</th>
                  <th className="px-6 py-3 section-title text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c, i) => (
                  <CandidateRow
                    key={c.id}
                    candidate={c}
                    index={i}
                    settings={settings}
                    mode={mode}
                    templateUrl={effectiveTemplateUrl}
                    isSelected={selectedIds.has(c.id)}
                    onToggleSelect={toggleSelectOne}
                    onDelete={id => {
                      deleteCandidate(id || c.id || c.generatedId);
                      setSelectedIds(prev => {
                        const next = new Set(prev);
                        next.delete(c.id);
                        return next;
                      });
                      toast.success(`Deleted ${c.name || 'candidate'}`);
                    }}
                    onDownload={downloadPDF}
                    onOpenEmailModal={setEmailModalCandidate}
                    onDirectSend={handleDirectSend}
                    onRegenerate={generateSingle}
                    sendingId={sendingCandidateId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Import Preview Modal ─────────────────────────────────── */}
      <AnimatePresence>
        {previewRows && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setPreviewRows(null)} />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="relative bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-7 py-5 border-b border-surface-100">
                <h2 className="font-semibold text-navy-900 text-lg">Import Preview</h2>
                <button onClick={() => setPreviewRows(null)} className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700">
                  <FiX className="w-4 h-4" />
                </button>
              </div>
              <div className="p-7 space-y-5">
                {/* Summary badges */}
                <div className="flex flex-wrap gap-3">
                  <span className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl text-xs font-black">
                    <FiCheck className="w-3.5 h-3.5" /> {previewRows.filter(r => !r._isDuplicate && r._emailValid).length} Valid
                  </span>
                  <span className="flex items-center gap-1.5 px-3.5 py-2 bg-amber-50 text-amber-700 border border-amber-100 rounded-xl text-xs font-black">
                    <FiAlertTriangle className="w-3.5 h-3.5" /> {previewRows.filter(r => r._isDuplicate).length} Duplicates
                  </span>
                  <span className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-50 text-rose-700 border border-rose-100 rounded-xl text-xs font-black">
                    <FiAlertCircle className="w-3.5 h-3.5" /> {previewRows.filter(r => !r._emailValid).length} Invalid Email
                  </span>
                </div>

                {/* Rows table */}
                <div className="overflow-auto max-h-64 rounded-2xl border border-slate-100">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left font-black text-slate-400 uppercase tracking-widest">Name</th>
                        <th className="px-4 py-3 text-left font-black text-slate-400 uppercase tracking-widest">Email</th>
                        <th className="px-4 py-3 text-left font-black text-slate-400 uppercase tracking-widest">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {previewRows.map((r, i) => (
                        <tr key={i} className={r._isDuplicate ? 'bg-amber-50/40' : !r._emailValid ? 'bg-rose-50/40' : ''}>
                          <td className="px-4 py-2.5 font-bold text-slate-800">{r._name}</td>
                          <td className="px-4 py-2.5 text-slate-500 font-mono">{r._email}</td>
                          <td className="px-4 py-2.5">
                            {r._isDuplicate
                              ? <span className="text-amber-600 font-black flex items-center gap-1"><FiAlertTriangle className="w-3 h-3"/>Duplicate</span>
                              : !r._emailValid
                              ? <span className="text-rose-600 font-black flex items-center gap-1"><FiAlertCircle className="w-3 h-3"/>Bad Email</span>
                              : <span className="text-emerald-600 font-black flex items-center gap-1"><FiCheck className="w-3 h-3"/>Ready</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end gap-3 pt-1">
                  <button onClick={() => setPreviewRows(null)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-200">
                    Cancel
                  </button>
                  <button onClick={confirmImport}
                    className="px-8 py-2.5 bg-sky-600 text-white rounded-xl font-bold text-sm hover:bg-sky-500 transition-all shadow-lg shadow-sky-500/20">
                    Import {previewRows.filter(r => !r._isDuplicate && r._emailValid).length} Valid Rows
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Single Email Modal ────────────────────────────────────── */}
      {emailModalCandidate && (
        <BrevoEmailModal
          candidate={emailModalCandidate}
          settings={settings}
          mode={mode}
          templateUrl={templatePreview || '/template.png'}
          onClose={() => setEmailModalCandidate(null)}
          onSent={handleCandidateEmailUpdate}
        />
      )}

      {/* ── Delete All Confirmation Modal ──────────────────────── */}
      {showDeleteAllConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-surface-200 text-center space-y-4">
            <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto border border-red-100 shadow-2xs">
              <FiTrash2 className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-950">Delete All Candidates?</h3>
              <p className="text-xs text-slate-600 font-semibold mt-1">
                Are you sure you want to delete all <strong className="text-red-600 font-bold">{candidates.length}</strong> candidate record(s)? This will clear the table and cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowDeleteAllConfirm(false)}
                className="flex-1 btn-secondary text-xs py-2.5 font-bold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteAllCandidates();
                  setShowDeleteAllConfirm(false);
                  toast.success('All candidate records deleted.');
                }}
                className="flex-1 btn-danger text-xs py-2.5 font-black shadow-sm"
              >
                Yes, Delete All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}